/**
 * Fabrique — inventaire et rotation des identifiants de longue durée.
 *
 * Le backend décrit chaque identifiant comme une LISTE de points de chute
 * auto-descriptifs, jamais comme une table indexée par identifiant de point de
 * chute : `json_response` camélise récursivement toutes les CLÉS, si bien
 * qu'un `sink_id` en position de clé reviendrait déformé. En valeur, il
 * traverse intact. (Même piège que payloadCase.ts et les intégrations.)
 *
 * Aucune valeur de secret ne circule dans le sens descendant : la Fabrique ne
 * peut pas relire un secret GitHub, et s'interdit de relire les paramètres
 * SSM. Ce que la page affiche, ce sont des dates — et la question à laquelle
 * elles répondent est « la dernière rotation a-t-elle atteint tous ses points
 * de chute ? », pas « la valeur est-elle la bonne ? ».
 */

import { apiCall } from "@/api/client";

/** Santé d'un identifiant vis-à-vis de son échéance. */
export type CredentialHealth =
  | "ok"
  | "warn"
  | "critical"
  | "expired"
  | "unknown";

/** D'où vient la date d'expiration retenue. */
export type ExpirySource = "discovered" | "declared" | "policy" | "unknown";

/**
 * État d'un point de chute.
 * - `in_sync`   : écrit par la Fabrique après la dernière rotation
 * - `stale`     : l'identifiant a été roté depuis la dernière écriture ici
 * - `drifted`   : la Fabrique l'a écrit, puis quelqu'un d'autre l'a modifié
 * - `unmanaged` : existe, mais la Fabrique ne l'a jamais écrit (état initial)
 * - `absent`    : n'existe pas
 * - `manual`    : hors de portée de la Fabrique
 * - `unknown`   : pas encore observé
 *
 * `unmanaged` et `drifted` sont distincts à dessein : la dérive est une
 * affirmation sur quelque chose que la Fabrique a fait, et elle ne peut pas
 * être portée sur un point de chute qu'elle n'a jamais écrit. Les confondre
 * affichait quinze alertes sur un parc parfaitement sain.
 */
export type SinkState =
  | "in_sync"
  | "stale"
  | "drifted"
  | "unmanaged"
  | "absent"
  | "manual"
  | "unknown";

export type SinkKind = "ssm" | "gha_repo" | "gha_org" | "manual";

export interface CredentialSink {
  id: string;
  kind: SinkKind;
  label: string;
  state: SinkState;
  propagatedAt: number | null;
  observedAt: number | null;
  remoteUpdatedAt: number | null;
  lastError: string | null;
  lastAttemptAt: number | null;
  /** Points de chute manuels uniquement — la marche à suivre. */
  instructions?: string;
}

export interface CredentialProbe {
  at: number | null;
  ok: boolean | null;
  message: string | null;
  rejected: boolean;
}

export interface Credential {
  id: string;
  label: string;
  type: string;
  description: string;
  /** Sa perte empêche la Fabrique de réparer quoi que ce soit côté GitHub. */
  bootstrap: boolean;
  health: CredentialHealth;
  expiresAt: number | null;
  expirySource: ExpirySource;
  daysLeft: number | null;
  rotatedAt: number | null;
  maxAgeDays: number | null;
  testable: boolean;
  /** Faux quand aucune copie maîtresse n'est relisible : il faudra recoller. */
  repropagatable: boolean;
  lastProbe: CredentialProbe | null;
  sinks: CredentialSink[];
}

export interface CredentialInventory {
  items: Credential[];
  summary: {
    total: number;
    needsAttention: number;
    unhealthySinks: number;
  };
  /**
   * Périmètre que la configuration du jeton d'amorçage doit couvrir.
   *
   * Une liste d'objets : le dépôt voyage en VALEUR, jamais en clé — la
   * camélisation récursive des clés le déformerait. Les raisons comptent
   * autant que le nom, les permissions n'étant pas les mêmes : « Secrets »
   * pour écrire un secret Actions, « Administration » pour lire et effacer
   * un runner.
   */
  githubScope: { repo: string; reasons: ("secrets" | "runners")[] }[];
}

export interface RotationResult {
  credentialId: string;
  rotated: boolean;
  written: number;
  total: number;
  expiresAt: number | null;
  results: {
    id: string;
    kind: SinkKind;
    label: string;
    ok: boolean;
    skipped: boolean;
    message: string;
  }[];
}

export interface ProbeResult {
  credentialId: string;
  ok: boolean;
  rejected: boolean;
  message: string;
}

export function listCredentials(token: string) {
  return apiCall<CredentialInventory>("/factory/credentials", { token });
}

export function getCredential(token: string, credentialId: string) {
  return apiCall<Credential>(
    `/factory/credentials/${encodeURIComponent(credentialId)}`,
    { token },
  );
}

/**
 * Propage une valeur vers les points de chute.
 *
 * Sans `value`, c'est la copie maîtresse qui est relue et re-propagée — ce qui
 * répare un point de chute ayant dérivé sans redemander le jeton. Avec
 * `value`, la valeur est d'abord éprouvée auprès du fournisseur : la propager
 * sans la vérifier remplacerait une panne sur un consommateur par une panne
 * sur tous.
 *
 * `sinkIds` restreint l'écriture à une partie des points de chute ; omis, ils
 * sont tous écrits.
 */
export function rotateCredential(
  token: string,
  credentialId: string,
  input: {
    value?: string;
    declaredExpiresAt?: number | null;
    sinkIds?: string[];
  },
) {
  return apiCall<RotationResult>(
    `/factory/credentials/${encodeURIComponent(credentialId)}/rotate`,
    {
      method: "POST",
      body: {
        value: input.value,
        declared_expires_at: input.declaredExpiresAt ?? null,
        sink_ids: input.sinkIds,
      },
      token,
    },
  );
}

/** Éprouve la valeur STOCKÉE auprès du fournisseur, pas celle du formulaire. */
export function testCredential(token: string, credentialId: string) {
  return apiCall<ProbeResult>(
    `/factory/credentials/${encodeURIComponent(credentialId)}/test`,
    { method: "POST", body: {}, token },
  );
}

/** Ré-observe les points de chute GitHub et la date d'expiration. */
export function refreshCredential(token: string, credentialId: string) {
  return apiCall<{
    credentialId: string;
    /** Un objet par point de chute observé — jamais une table indexée par id. */
    observed: {
      id: string;
      exists: boolean;
      remoteUpdatedAt: number | null;
    }[];
    discoveredExpiresAt: number | null;
  }>(`/factory/credentials/${encodeURIComponent(credentialId)}/refresh`, {
    method: "POST",
    body: {},
    token,
  });
}
